import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import ProductsPage from 'src/pages/products/ProductsPage';
import ProductPage from 'src/pages/product/ProductPage';
import LoginPage from 'src/pages/login/LoginPage';
import ChangeLogPageWrapper from 'src/pages/changelog/ChangeLogPageWrapper';
import AdminPage from 'src/pages/admin/AdminPage';
import { PrivateRoute } from './PrivateRoute';
import Layout from 'src/components/common/Layout';
import { AppWrapper, PageWrapper, ContentWrapper } from '../App.styles';
import ToolsPage from 'src/pages/tools/ToolsPage';
import DocumentsPage from 'src/pages/documents/DocumentsPage';
import { useAuthStore } from '../context/AuthContext';
import useMsal from '../auth/useMsal';
import useGetUserRoles from '../api/useGetUserRoles';
import useGetAppsMenu from '../hooks/useGetAppsMenu';
import useGetCharterers from '../hooks/useGetCharterers';

// Wrapper component for protected routes with shared layout
const ProtectedLayout = ({ children }: { children: React.ReactNode }) => (
    <PrivateRoute>
        <PageWrapper>
            <Layout>
                <ContentWrapper>{children}</ContentWrapper>
            </Layout>
        </PageWrapper>
    </PrivateRoute>
);

// Wrapper component for routes restricted to writer roles
const WriterProtectedLayout = ({ children }: { children: React.ReactNode }) => {
    const { canEdit, canEditChemGuard } = useAuthStore();
    if (!(canEdit || canEditChemGuard)) {
        return (
            <PrivateRoute>
                <Navigate to="/products" replace />
            </PrivateRoute>
        );
    }
    return <ProtectedLayout>{children}</ProtectedLayout>;
};

const RouterContent = () => {
    const location = useLocation();
    const { checkAuthStatus, initHandleRedirect, isInitialized } = useMsal();
    const { loggedInUser } = useAuthStore();
    const { getUserRoles } = useGetUserRoles();
    const { getAppsMenu } = useGetAppsMenu();
    const { getCharterers } = useGetCharterers();
    const { triggerAllQueryParamsFromUrl } = useQueryParamsHandler();
    const [, setIsAuthorized] = useState<boolean | undefined>(undefined);

    // Track whether we've already initiated auth check to prevent duplicate calls
    const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
    const [hasLoadedRoles, setHasLoadedRoles] = useState(false);

    // Reset hasLoadedRoles when user logs out (loggedInUser becomes null)
    useEffect(() => {
        if (!loggedInUser) {
            setHasLoadedRoles(false);
        }
    }, [loggedInUser]);

    // Trigger query params update when location changes (like OrcaWebsite)
    useEffect(() => {
        triggerAllQueryParamsFromUrl();
    }, [location]);

    // Like OrcaWebsite: Simple dependency on loggedInUser and isInitialized
    // All data fetch functions run in parallel when user is authenticated
    useEffect(() => {
        if (loggedInUser) {
            setIsAuthorized(true);

            // Only call getUserRoles once per auth session
            if (!hasLoadedRoles) {
                getUserRoles();
                setHasLoadedRoles(true);
            }

            // Fetch apps menu and charterers (non-blocking, runs in parallel with other calls)
            getAppsMenu();
            getCharterers();
        } else if (isInitialized && !hasCheckedAuth) {
            // Only call checkAuthStatus after MSAL is initialized
            setHasCheckedAuth(true);
            checkAuthStatus();
        }
    }, [loggedInUser, isInitialized, hasLoadedRoles]);

    useEffect(() => {
        // Initialize redirect handling when MSAL is ready (like OrcaWebsite)
        if (isInitialized) {
            initHandleRedirect();
        }
    }, [isInitialized]);

    // Don't show global loading - LoginPage handles loading overlay
    // This allows LoginPage to be visible in background while authenticating

    return (
        <AppWrapper>
            <Routes>
                {/* Public login route */}
                <Route path="/login" element={<LoginPage />} />

                {/* Default route redirects to products */}
                <Route path="/" element={<Navigate to="/products" replace />} />

                {/* Protected application routes */}
                <Route
                    path="/products"
                    element={
                        <ProtectedLayout>
                            <ProductsPage />
                        </ProtectedLayout>
                    }
                />
                <Route
                    path="/documents"
                    element={
                        <ProtectedLayout>
                            <DocumentsPage />
                        </ProtectedLayout>
                    }
                />
                <Route
                    path="/product"
                    element={
                        <ProtectedLayout>
                            <ProductPage />
                        </ProtectedLayout>
                    }
                />
                <Route
                    path="/tools"
                    element={
                        <ProtectedLayout>
                            <ToolsPage />
                        </ProtectedLayout>
                    }
                />
                <Route
                    path="/changelog"
                    element={
                        <WriterProtectedLayout>
                            <ChangeLogPageWrapper />
                        </WriterProtectedLayout>
                    }
                />
                <Route
                    path="/admin"
                    element={
                        <WriterProtectedLayout>
                            <AdminPage />
                        </WriterProtectedLayout>
                    }
                />

                {/* Catch-all route for 404 - redirect to products if authenticated, login if not */}
                <Route
                    path="*"
                    element={
                        <ProtectedLayout>
                            <Navigate to="/products" replace />
                        </ProtectedLayout>
                    }
                />
            </Routes>
        </AppWrapper>
    );
};

export default RouterContent;
